import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { ORDER_TYPE_IDS, PRODUCT_IDS, PRODUCT_TYPE_IDS } from "@chef/constants";
import { isEqualStrings, isNotEqualStrings } from "@chef/utils/equal";
import {
  isGroceryBasketType,
  isMealboxBasketType,
  isOneSubProduct,
  isPickAndMixProduct,
} from "@chef/helpers";
import { isNotEmptyArray } from "@chef/utils/array";
import type { WeekYear } from "@chef/types/common";

import { api } from "../graphql/api";
import {
  CalendarQueryVariables,
  OrdersQueryVariables,
} from "../graphql/generated";
import { addResetEndpointOnLogin } from "./auth";
import {
  addResetEndpointOnUpdateBilling,
  addResetEndpointOnUpdateCalendar,
} from "./billing";
import { addResetEndpointOnDirectOrderUpdates } from "./directOrder";
import type { RootState } from "..";
import {
  findVariationByMealsAndPortions,
  getAttribute,
  getPriceOfProduct,
  getPriceOfProductByVariation,
} from "../helpers";

export interface IDeviationProduct {
  quantity: number;
  price: number;
  variationId: string;
  productId: string;
  productTypeId: string;
  name?: string;
  allergenComplianceId?: string;
}

export interface IDeviationBasket {
  week: number;
  year: number;
  isDirty?: boolean;
  products: IDeviationProduct[];
  editMode?: boolean;
}

export interface IHasSetInitialPriceData {
  week: number;
  year: number;
  set: boolean;
}
export interface ICampaignProduct {
  quantity: number;
  price: number;
  variationId: string;
  productTypeId: string;
  productId: string;
}
export interface ICampaignBasket {
  week: number;
  year: number;
  products: ICampaignProduct[];
}

interface RecipePresetProduct {
  recipeId: string;
  variationId?: never;
  productTypeId: typeof PRODUCT_TYPE_IDS["PICKANDMIX"];
}

interface GroceryPresetProduct {
  recipeId?: never;
  variationId: string;
  productTypeId:
    | typeof PRODUCT_TYPE_IDS["STANDALONE"]
    | typeof PRODUCT_TYPE_IDS["ADDON"];
  quantity: number;
}

type PresetDeviationProduct = RecipePresetProduct | GroceryPresetProduct;

export interface PresetDeviation {
  week: number | null;
  year: number | null;
  products: PresetDeviationProduct[];
}

const initialState = {
  defaultBasketInitialized: false,
  hasSetInitialPriceData: [] as IHasSetInitialPriceData[],
  addonProducts: [] as IDeviationProduct[],

  deviations: [] as IDeviationBasket[],
  currentlySubmittingQuickAddDeviationVariationId: "",
  currentlySubmittingQuickAddDeviationProducts: [] as IDeviationProduct[],

  campaignBaskets: [] as ICampaignBasket[],
  defaultCampaignBasketInitialized: false,

  presetDeviation: {
    week: null,
    year: null,
    products: [],
  } as PresetDeviation,
};

// NOTE: Needs to be called before using addonProducts
export const loadDefaultBasketAddonProducts = createAsyncThunk(
  "basket/loadDefaultBasketAddonProducts",
  async (_, { dispatch }) => {
    const { billing } = await dispatch(
      api.endpoints.billing.initiate(),
    ).unwrap();
    if (!billing) {
      return;
    }

    const defaultBasket = billing.baskets.find(isGroceryBasketType);
    if (!defaultBasket) {
      console.error("Unreachable state reached: no default basket found");
      return;
    }
    dispatch(
      basketSlice.actions.addInitialAddonProducts(
        defaultBasket.basketProducts.map((bp) => ({
          productId: bp.variation.productId,
          productTypeId: bp.variation.product.productTypeId,
          variationId: bp.variationId,
          price: bp.variation.finalPrice,
          quantity: bp.quantity,
        })),
      ),
    );
  },
);

// NOTE: needs to be called before using deviation
export const loadCalendar = createAsyncThunk(
  "basket/loadCalendar",
  async (args: CalendarQueryVariables, { dispatch, getState }) => {
    const presetDeviation = (getState() as RootState).basket.presetDeviation;

    const { week, year, products: presetProducts } = presetDeviation;

    let primaryProduct: IDeviationBasket["products"][number] | null = null;

    let presetRecipesDeviation: IDeviationBasket["products"] | undefined =
      undefined;
    let presetGroceriesDeviation: IDeviationBasket["products"] | undefined =
      undefined;

    let shouldLoadPresetDeviation = false;

    if (isNotEmptyArray(presetProducts) && week && year) {
      shouldLoadPresetDeviation =
        presetDeviation.week === week && presetDeviation.year === year;
    }

    const skip = !shouldLoadPresetDeviation;

    const { calendar } = await dispatch(
      api.endpoints.calendar.initiate(args),
    ).unwrap();

    const pickAndMix = await (async () => {
      if (!skip) {
        const { pickAndMix } = await dispatch(
          api.endpoints.pickAndMix.initiate({
            productTypeId: PRODUCT_TYPE_IDS.PICKANDMIX,
            week: week || 0,
            year: year || 0,
          }),
        ).unwrap();
        return pickAndMix;
      } else {
        return null;
      }
    })();

    const products = await (async () => {
      if (!skip) {
        const { products } = await dispatch(
          api.endpoints.productsByIds.initiate({
            productIds: [PRODUCT_IDS.ONESUB],
            week: week || 0,
            year: year || 0,
          }),
        ).unwrap();
        return products;
      } else {
        return null;
      }
    })();

    // if we have a preset, we need to load the products for the preset
    if (shouldLoadPresetDeviation) {
      const calendarForThePreselectedWeek = calendar?.find(
        (c) => c.week === week && c.year === year,
      );

      const mealboxBasket =
        calendarForThePreselectedWeek?.baskets.find(isMealboxBasketType);

      const subscribedMealboxProduct =
        mealboxBasket?.basketDetails.products.find((p) =>
          isOneSubProduct(p.variation.product),
        )?.variation;

      const variationIds = presetProducts
        .filter((p) =>
          isNotEqualStrings(p.productTypeId, PRODUCT_TYPE_IDS.PICKANDMIX),
        )
        .filter((p) => p.variationId !== undefined)
        .map((p) => p.variationId);

      const { products: productsByVariationIds } = await dispatch(
        api.endpoints.productsByVariationIds.initiate({
          variationIds,
          week: week || 0,
          year: year || 0,
        }),
      ).unwrap();

      const preselectedGroceryProducts = productsByVariationIds?.flatMap(
        (product) => {
          const { productId, productTypeId } = product;

          return product.variations.flatMap((variation) => {
            return {
              variationId: variation.variationId,
              productTypeId,
              productId,
              price: getPriceOfProductByVariation(variation),
            };
          });
        },
      );

      const preselectedRecipes = presetProducts.filter(isPickAndMixProduct);

      const oneSubProduct = products?.find(isOneSubProduct);

      // portions and meals for the preset products
      const portions =
        (subscribedMealboxProduct &&
          +(getAttribute("Portions", subscribedMealboxProduct) || "0")) ||
        0;

      const meals = preselectedRecipes?.length;

      const variations = products?.find(isOneSubProduct)?.variations || [];

      const newOneSubProduct = findVariationByMealsAndPortions({
        variations,
        portions,
        meals,
      });

      const preselectedPickAndMix = pickAndMix?.filter(({ recipes }) =>
        recipes.some(({ mainRecipeId }) =>
          preselectedRecipes.some(
            ({ recipeId }) => recipeId && +recipeId === mainRecipeId,
          ),
        ),
      );

      presetRecipesDeviation = preselectedPickAndMix?.map((recipe) => {
        const { productId, productTypeId, product, recipes } = recipe;

        const matchingPortion = recipes
          .flatMap((r) => r.portions.filter((p) => p.size === portions))
          .find((p) => p.size === portions);

        if (!matchingPortion) {
          throw new Error("No matching portion found");
        }

        const variationId = matchingPortion.variationId;

        const price = getPriceOfProduct({
          product,
          variationId,
        });

        return {
          productId,
          productTypeId,
          variationId,
          quantity: 1,
          price,
        };
      });

      presetGroceriesDeviation = preselectedGroceryProducts?.map((product) => {
        const { productId, productTypeId, variationId, price } = product;

        return {
          productId,
          productTypeId,
          variationId,
          quantity: 1,
          price,
        };
      });

      if (!oneSubProduct || !newOneSubProduct) {
        return;
      }
      primaryProduct = {
        productId: oneSubProduct.productId,
        productTypeId: PRODUCT_TYPE_IDS.MEALBOX,
        price: newOneSubProduct?.flexiblePriceV2.regularPrice,
        variationId: newOneSubProduct.variationId,
        quantity: 1,
      };
    }

    if (!calendar) {
      return;
    }

    for (const { week, year, baskets, preselectorBasket } of calendar) {
      let productDeviations = [] as IDeviationBasket["products"];
      for (const {
        basketDetails: { products },
      } of baskets) {
        for (const product of products) {
          const isPreselected = preselectorBasket?.variationIds.some((v) =>
            isEqualStrings(v, product.variationId),
          );
          productDeviations.push({
            quantity: product.quantity,
            variationId: product.variationId,
            productId: product.variation.productId,
            productTypeId: product.variation.product.productTypeId,
            price: isPreselected
              ? 0
              : getPriceOfProductByVariation(product.variation),
            allergenComplianceId: product.allergenComplianceId,
          });
        }

        // Overwrite the deviations with the preset deviations
        if (presetDeviation.week === week && presetDeviation.year === year) {
          productDeviations = [];

          if (!primaryProduct || !presetRecipesDeviation) {
            return;
          }

          productDeviations.push(
            ...presetRecipesDeviation,
            primaryProduct,
            ...(presetGroceriesDeviation ?? []),
          );

          dispatch(
            basketSlice.actions.setDeviation({
              basket: {
                week,
                year,
                products: productDeviations,
                editMode: false,
              },
            }),
          );

          dispatch(resetPresetDeviation());
          continue;
        }

        dispatch(
          basketSlice.actions.setHasSetInitialPrice({
            week,
            year,
            set: true,
          }),
        );
      }

      dispatch(
        basketSlice.actions.addInitialDeviation({
          week,
          year,
          products: productDeviations,
        }),
      );
    }
  },
);

export const loadCampaignBasket = createAsyncThunk(
  "basket/loadCampaignBasket",
  async (
    args: OrdersQueryVariables & {
      slug: string;
      week: number;
      year: number;
    },
    { dispatch },
  ) => {
    const { slug, week, year, ...rest } = args;

    const ordersQuery = await dispatch(
      api.endpoints.orders.initiate(rest),
    ).unwrap();

    const { productCategories } = await dispatch(
      api.endpoints.productCategories.initiate(),
    ).unwrap();

    const categories = productCategories.filter(
      ({ hasProducts, completeSlug }) =>
        hasProducts && completeSlug.startsWith(slug),
    );

    const categoryIds = categories.map((c) => c.productCategoryId);
    const { productsByCategories } = await dispatch(
      api.endpoints.productsByCategories.initiate({
        categoryIds,
        week,
        year,
      }),
    ).unwrap();

    const orders = ordersQuery?.orders.filter((order) =>
      isEqualStrings(
        order.orderTypeId.toLowerCase(),
        ORDER_TYPE_IDS.CAMPAIGN.toLowerCase(),
      ),
    );

    const baskets: ICampaignBasket[] = [];

    for (const { week, year, orderLines } of orders) {
      const basket = [] as ICampaignBasket["products"];
      const products = productsByCategories.flatMap((pbc) => pbc.products);
      for (const ol of orderLines) {
        const product = products.find((product) =>
          product.variations.find((v) => v.variationId === ol.variationId),
        );
        if (!product) {
          continue;
        }

        basket.push({
          quantity: ol.quantity,
          variationId: ol.variationId,
          price: +ol.pricePerQuantity,
          productTypeId: product.productTypeId,
          productId: product.productId,
        });
      }

      baskets.push({
        week,
        year,
        products: basket,
      });
    }

    dispatch(basketSlice.actions.addInitialCampaignBaskets(baskets));
  },
);

export const basketSlice = createSlice({
  name: "basket",
  initialState,
  reducers: {
    // default/addon basket
    addInitialAddonProducts: (
      state,
      { payload }: PayloadAction<IDeviationProduct[]>,
    ) => {
      if (!state.defaultBasketInitialized) {
        state.addonProducts = payload;

        state.defaultBasketInitialized = true;
      }
    },

    setAddonProducts: (
      state,
      { payload }: PayloadAction<IDeviationProduct[]>,
    ) => {
      state.addonProducts = payload;
    },

    resetDefaultAddonProducts: (state) => ({
      ...state,
      defaultBasketInitialized: false,
      addonProducts: [],
    }),

    resetAddonProducts: (state) => ({
      ...state,
      addonProducts: [],
      defaultBasketInitialized: false,
    }),

    // initial price data
    setHasSetInitialPrice: (
      state,
      { payload }: PayloadAction<IHasSetInitialPriceData>,
    ) => {
      const data = state.hasSetInitialPriceData.find(
        (d) => d.week === payload.week && d.year === payload.year,
      );

      if (!data) {
        // shouldn't be possible to happen in theory
        state.hasSetInitialPriceData.push(payload);
        return;
      }

      data.set = payload.set;
    },

    // deviation/calendar
    addInitialDeviation: (
      state,
      { payload }: PayloadAction<Omit<IDeviationBasket, "isDirty">>,
    ) => {
      const { week, year } = payload;

      const idx = state.deviations.findIndex(
        (d) => d.week === week && d.year === year,
      );
      // we already have it
      if (idx !== -1) {
        return;
      }

      state.deviations.push({ ...payload, isDirty: false, editMode: false });

      state.hasSetInitialPriceData.push({ week, year, set: false });
    },

    // Preset basket deviation
    setPresetDeviation: (
      state,
      { payload }: PayloadAction<PresetDeviation>,
    ) => {
      state.presetDeviation = payload;
    },

    resetPresetDeviation: (state) => {
      state.presetDeviation = {
        week: null,
        year: null,
        products: [],
      } as PresetDeviation;
    },

    setDeviation: (
      state,
      {
        payload,
      }: PayloadAction<{
        basket: Omit<IDeviationBasket, "isDirty">;
        manual?: boolean;
      }>,
    ) => {
      const idx = state.deviations.findIndex(
        (d) => d.week === payload.basket.week && d.year === payload.basket.year,
      );

      const newDeviation = {
        ...payload.basket,
        isDirty: payload.manual ?? false,
        editMode: payload.basket.editMode ?? true,
      };

      if (idx === -1) {
        state.deviations.push(newDeviation);
      } else {
        state.deviations[idx] = {
          ...newDeviation,
          isDirty: payload.manual ?? state.deviations[idx].isDirty,
        };
      }
    },

    addProductToDeviation: (
      state,
      {
        payload,
      }: PayloadAction<{
        week: number;
        year: number;
        product: IDeviationProduct;
      }>,
    ) => {
      const idx = state.deviations.findIndex(
        (d) => d.week === payload.week && d.year === payload.year,
      );

      if (idx !== -1) {
        const deviation = state.deviations[idx];
        deviation.isDirty = true;
        deviation.editMode = true;

        const productIdx = deviation.products.findIndex(
          (p) => p.productId === payload.product.productId,
        );
        if (productIdx === -1) {
          deviation.products.push(payload.product);
        }
      }
    },
    removeProductFromDeviation: (
      state,
      {
        payload,
      }: PayloadAction<{
        week: number;
        year: number;
        product: IDeviationProduct;
      }>,
    ) => {
      const idx = state.deviations.findIndex(
        (d) => d.week === payload.week && d.year === payload.year,
      );

      if (idx !== -1) {
        const deviation = state.deviations[idx];
        deviation.isDirty = true;
        deviation.editMode = true;

        const productIdx = deviation.products.findIndex(
          (p) => p.productId === payload.product.productId,
        );

        if (productIdx !== -1) {
          deviation.products.splice(productIdx, 1);
        }
      }
    },
    updateAddonToDeviation: (
      state,
      {
        payload,
      }: PayloadAction<{
        week: number;
        year: number;
        product: IDeviationBasket["products"][0];
      }>,
    ) => {
      const deviation = state.deviations.find(
        (d) => d.week === payload.week && d.year === payload.year,
      );
      if (!deviation) {
        return;
      }
      deviation.isDirty = true;
      deviation.editMode = true;

      const { productId, variationId } = payload.product;

      const idx = deviation.products.findIndex(
        (p) => p.productId === productId && p.variationId === variationId,
      );
      if (idx === -1 && payload.product.quantity > 0) {
        deviation.products.push(payload.product);
        return;
      }

      if (payload.product.quantity === 0) {
        deviation.products.splice(idx, 1);
        return;
      }

      deviation.products[idx] = payload.product;
    },

    setCurrentlySubmittingQuickAddDeviationVariationId: (
      state,
      action: PayloadAction<{ variationId: string }>,
    ) => {
      state.currentlySubmittingQuickAddDeviationVariationId =
        action.payload.variationId;
    },

    setCurrentlySubmittingQuickAddDeviationProduct: (
      state,
      action: PayloadAction<{ deviationProduct: IDeviationProduct }>,
    ) => {
      const { deviationProduct } = action.payload;

      const existingDeviationProductIndex =
        state.currentlySubmittingQuickAddDeviationProducts.findIndex(
          (product) => product.variationId === deviationProduct.variationId,
        );

      if (existingDeviationProductIndex !== -1) {
        if (deviationProduct.quantity === 0) {
          state.currentlySubmittingQuickAddDeviationProducts.splice(
            existingDeviationProductIndex,
            1,
          );
        } else {
          state.currentlySubmittingQuickAddDeviationProducts[
            existingDeviationProductIndex
          ] = deviationProduct;
        }
      } else {
        if (deviationProduct.quantity > 0) {
          state.currentlySubmittingQuickAddDeviationProducts.push(
            deviationProduct,
          );
        }
      }
    },

    resetCurrentlySubmittingQuickAddDeviationProducts: (state) => ({
      ...state,
      currentlySubmittingQuickAddDeviationProducts: [],
      currentlySubmittingQuickAddDeviationVariationId: "",
    }),

    resetDeviations: (state) => ({
      ...state,
      deviations: [],
      hasSetInitialPriceData: [],
    }),

    resetDeviationsForWeek: (
      state,
      action: PayloadAction<{ week: number; year: number }>,
    ) => {
      const { week, year } = action.payload;
      state.deviations = state.deviations.filter(
        (d) => d.week !== week || d.year !== year,
      );
      state.hasSetInitialPriceData = state.hasSetInitialPriceData.filter(
        (d) => d.week !== week || d.year !== year,
      );
    },

    // CampaignBasket
    addInitialCampaignBaskets: (
      state,
      { payload }: PayloadAction<ICampaignBasket[]>,
    ) => {
      if (!state.campaignBaskets) {
        state.campaignBaskets = [];
      }

      for (const basket of payload) {
        const { week, year } = basket;
        const idx = state.campaignBaskets.findIndex(
          (d) => d.week === week && d.year === year,
        );

        // we already have it
        if (idx !== -1) {
          continue;
        }

        state.campaignBaskets.push(basket);
      }

      state.defaultCampaignBasketInitialized = true;
    },

    addProductToCampaignBasket: (
      state,
      {
        payload,
      }: PayloadAction<{
        product: Omit<ICampaignProduct, "quantity">;
        week: number;
        year: number;
      }>,
    ) => {
      const {
        week,
        year,
        product: { productId, variationId, price, productTypeId },
      } = payload;

      const idx = state.campaignBaskets.findIndex(
        (d) => d.week === week && d.year === year,
      );
      if (idx === -1) {
        state.campaignBaskets.push({
          week: week,
          year: year,
          products: [
            { productId, variationId, price, productTypeId, quantity: 1 },
          ],
        });
      } else {
        const basket = state.campaignBaskets[idx];

        const productIdx = basket.products.findIndex(
          (p) => p.productId === productId && p.variationId === variationId,
        );

        if (productIdx === -1) {
          basket.products.push({
            productId,
            variationId,
            price,
            productTypeId,
            quantity: 1,
          });
        } else {
          basket.products[productIdx].quantity++;
        }
      }
    },

    removeProductFromCampaignBasket: (
      state,
      {
        payload,
      }: PayloadAction<{
        product: Pick<ICampaignProduct, "variationId">;
        week: number;
        year: number;
      }>,
    ) => {
      const {
        week,
        year,
        product: { variationId },
      } = payload;

      const basket = state.campaignBaskets.find(
        (d) => d.week === week && d.year === year,
      );

      if (!basket) {
        return;
      }

      const productIdx = basket.products.findIndex(
        (p) => p.variationId === variationId,
      );

      if (productIdx === -1) {
        return;
      }

      basket.products[productIdx].quantity--;

      if (basket.products[productIdx].quantity === 0) {
        basket.products.splice(productIdx, 1);
      }
    },

    updateGroceryProductFromCampaignBasket: (
      state,
      {
        payload,
      }: PayloadAction<ICampaignProduct & { week: number; year: number }>,
    ) => {
      const {
        variationId,
        price,
        productId,
        productTypeId,
        quantity,
        week,
        year,
      } = payload;

      const idx = state.campaignBaskets.findIndex(
        (d) => d.week === week && d.year === year,
      );

      if (idx === -1) {
        return;
      }
      const basket = state.campaignBaskets[idx];

      const productIdx = basket.products.findIndex(
        (p) => p.variationId === variationId,
      );

      if (productIdx === -1) {
        basket.products.push({
          productId,
          variationId,
          price,
          productTypeId,
          quantity: 1,
        });
      } else if (quantity === 0) {
        basket.products.splice(productIdx, 1);
      } else {
        basket.products[productIdx].quantity = quantity;
      }
    },

    resetCampaignBaskets: (state) => ({
      ...state,
      campaignBaskets: [],
      defaultCampaignBasketInitialized: false,
    }),

    toggleEditModeForWeek: (
      state,
      action: PayloadAction<{ week: number; year: number }>,
    ) => {
      const deviation = state.deviations.find(
        (d) => d.week === action.payload.week && d.year === action.payload.year,
      );

      if (deviation) {
        deviation.editMode = !deviation.editMode;
      }
    },

    setEditModeForWeek: (
      state,
      action: PayloadAction<{ week: number; year: number; editMode: boolean }>,
    ) => {
      const deviation = state.deviations.find(
        (d) => d.week === action.payload.week && d.year === action.payload.year,
      );

      if (deviation) {
        deviation.editMode = action.payload.editMode;
      }
    },
  },
});

// this will handle resetting the state when the user changes billing
addResetEndpointOnUpdateBilling(basketSlice.actions.resetDefaultAddonProducts);

// this will handle resetting the state when the user changes calendar
addResetEndpointOnUpdateCalendar(basketSlice.actions.resetDeviations);

// this will handle resetting the state when the user submits a direct order
addResetEndpointOnDirectOrderUpdates(basketSlice.actions.resetCampaignBaskets);

// this will handle resetting the state when user logs in
addResetEndpointOnLogin(basketSlice.actions.resetDeviations);
addResetEndpointOnLogin(basketSlice.actions.resetCampaignBaskets);
addResetEndpointOnLogin(basketSlice.actions.resetAddonProducts);

export const {
  setAddonProducts,
  setDeviation,
  addProductToDeviation,
  removeProductFromDeviation,
  setHasSetInitialPrice,
  updateAddonToDeviation,
  resetDeviations,
  resetDeviationsForWeek,
  addProductToCampaignBasket,
  removeProductFromCampaignBasket,
  updateGroceryProductFromCampaignBasket,
  setPresetDeviation,
  resetPresetDeviation,
  setCurrentlySubmittingQuickAddDeviationProduct,
  setCurrentlySubmittingQuickAddDeviationVariationId,
  resetCurrentlySubmittingQuickAddDeviationProducts,
  toggleEditModeForWeek,
  setEditModeForWeek,
  addInitialDeviation,
} = basketSlice.actions;

export const selectAddonProducts = (state: RootState) =>
  state.basket.addonProducts.filter((product) =>
    (
      [
        PRODUCT_TYPE_IDS.STANDALONE,
        PRODUCT_TYPE_IDS.ADDON,
        PRODUCT_TYPE_IDS.LIMITED_QUANTITY,
      ] as string[]
    ).includes(product.productTypeId),
  );

export const selectDeviationBasket =
  ({ week, year }: { week: number; year: number }) =>
  (state: RootState) =>
    state.basket.deviations.find((d) => d.week === week && d.year === year);

/**
 * NOTE: this requires useCreateDeviationBasket to be ran
 */
export const selectDeviationProducts =
  ({ week, year }: { week: number; year: number }) =>
  (state: RootState) =>
    state.basket.deviations.find((d) => d.week === week && d.year === year)
      ?.products;

export const selectHasSetInitialPrice =
  ({ week, year }: { week: number; year: number }) =>
  (state: RootState) =>
    state.basket.hasSetInitialPriceData.find(
      (d) => d.week === week && d.year === year,
    )?.set || false;

export const selectCampaignBaskets =
  ({ week, year }: { week: number; year: number }) =>
  (state: RootState) =>
    state.basket.campaignBaskets?.find(
      (d) => d.week === +week && d.year === +year,
    );

export const selectIsCampaignBasketInitialized = (state: RootState) =>
  state.basket.defaultCampaignBasketInitialized;

export const selectIsEditModeOnForWeek =
  ({ week, year }: WeekYear) =>
  (state: RootState): boolean =>
    state.basket.deviations.find(
      (d) => d.week === week && d.year === year && d.editMode,
    ) !== undefined;
